import { apiClient } from "@/lib/apiClient";

const BASE = "/api/admin/reports/certificates";

export interface CertificateHierarchyFilters {
  dateFrom?: string;
  dateTo?: string;
  departmentIds?: string[];
  search?: string;
  page?: number;
  limit?: number;
}

export interface HierarchyPageMeta {
  total: number;
  page: number;
  limit: number;
}

export interface Level1Item {
  certificateId: string;
  certificateName: string;
  educationsCount: number;
  totalEnrollments: number;
  issuedCount: number;
  pendingCount: number;
  issueRatePercent: number;
}

export interface Level2Item {
  educationId: string;
  educationName: string;
  code?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  totalEnrollments: number;
  issuedCount: number;
  pendingCount: number;
  issueRatePercent: number;
}

export interface Level3Item {
  departmentId: string;
  departmentName: string;
  totalEnrollments: number;
  issuedCount: number;
  pendingCount: number;
  issueRatePercent: number;
}

export interface Level4Item {
  enrollmentId: string;
  userId: string;
  fullName: string;
  identityNumber?: string | null;
  title?: string | null;
  enrollmentStatus: number;
  hasCertificate: boolean;
  certificateNumber?: string | null;
  issuedAt?: string | null;
  examScorePercentage?: number | null;
}

function buildQuery(
  filters?: CertificateHierarchyFilters,
): Record<string, unknown> {
  const q: Record<string, unknown> = {};
  if (filters?.dateFrom) q.dateFrom = filters.dateFrom;
  if (filters?.dateTo) q.dateTo = filters.dateTo;
  if (filters?.departmentIds?.length) q.departmentIds = filters.departmentIds;
  if (filters?.search?.trim()) q.search = filters.search.trim();
  if (filters?.page) q.page = filters.page;
  if (filters?.limit) q.limit = filters.limit;
  return q;
}

type Response<T> = T & HierarchyPageMeta & { items: unknown[] };

export const certificateHierarchyApi = {
  getLevel1: async (
    filters?: CertificateHierarchyFilters,
  ): Promise<{ items: Level1Item[] } & HierarchyPageMeta> => {
    const { data } = await apiClient.get<
      { data: { items: Level1Item[] } & HierarchyPageMeta } | (Response<unknown> & unknown)
    >(`${BASE}/hierarchy`, { params: buildQuery(filters) });
    const payload = (data as { data?: unknown })?.data ?? data;
    return payload as { items: Level1Item[] } & HierarchyPageMeta;
  },

  getLevel2: async (
    certificateId: string,
    filters?: CertificateHierarchyFilters,
  ): Promise<{ items: Level2Item[] } & HierarchyPageMeta> => {
    const { data } = await apiClient.get<{ data?: unknown } | unknown>(
      `${BASE}/${certificateId}/educations`,
      { params: buildQuery(filters) },
    );
    const payload = (data as { data?: unknown })?.data ?? data;
    return payload as { items: Level2Item[] } & HierarchyPageMeta;
  },

  getLevel3: async (
    certificateId: string,
    educationId: string,
    filters?: CertificateHierarchyFilters,
  ): Promise<{ items: Level3Item[] } & HierarchyPageMeta> => {
    const { data } = await apiClient.get<{ data?: unknown } | unknown>(
      `${BASE}/${certificateId}/educations/${educationId}/departments`,
      { params: buildQuery(filters) },
    );
    const payload = (data as { data?: unknown })?.data ?? data;
    return payload as { items: Level3Item[] } & HierarchyPageMeta;
  },

  getLevel4: async (
    certificateId: string,
    educationId: string,
    departmentId: string,
    filters?: CertificateHierarchyFilters,
  ): Promise<{ items: Level4Item[] } & HierarchyPageMeta> => {
    const { data } = await apiClient.get<{ data?: unknown } | unknown>(
      `${BASE}/${certificateId}/educations/${educationId}/departments/${departmentId}/users`,
      { params: buildQuery(filters) },
    );
    const payload = (data as { data?: unknown })?.data ?? data;
    return payload as { items: Level4Item[] } & HierarchyPageMeta;
  },
};
